<template>
  <div class="caPopup">
    <div class="managerPopup">
      <div class="managerPopup-top caPopupHead">
        <div>
          <h3 class="managerPopup-title">Chiffre d'affaires</h3>
          <div class="managerPopup-subtitle">
            Suivez rapidement le total des factures et la répartition mensuelle sur l'exercice en cours.
          </div>
        </div>
        <div class="caPopupBadge">{{ exerciceLabel }}</div>
      </div>

      <div class="managerPopup-card caFilterCard">
        <div class="caFilterGrid">
          <div class="caFilterField datePick">
            <label>Date début</label>
            <b-form-datepicker
              id="in-datepicker"
              placeholder="Date début"
              selected-variant="primary"
              today-variant="primary"
              nav-button-variant="secondary"
              variant="danger"
              v-model="dateIn"
            ></b-form-datepicker>
          </div>

          <div class="caFilterField datePick">
            <label>Date de fin</label>
            <b-form-datepicker
              id="out-datepicker"
              placeholder="Date de fin"
              selected-variant="primary"
              today-variant="primary"
              nav-button-variant="secondary"
              variant="danger"
              v-model="dateOut"
            ></b-form-datepicker>
          </div>

          <div class="caFilterActions">
            <b-button
              size="sm"
              variant="outline-secondary"
              class="caFilterBtn"
              @click="resetFilters()"
              :disabled="!hasActiveFilter"
            >
              Reset
            </b-button>
            <b-button
              size="sm"
              variant="success"
              class="caFilterBtn"
              @click="parseList()"
            >
              Filtrer
            </b-button>
          </div>
        </div>

        <div class="caFilterMeta">
          <span>{{ filterSummary }}</span>
        </div>
      </div>

      <div class="caStatsGrid">
        <div class="managerPopup-card caStatCard">
          <div class="caStatEyebrow">Total factures</div>
          <div class="caStatValue">{{ totalCA }}</div>
          <div class="caStatUnit">dinars TTC</div>

          <div class="caMonthlyList" v-if="activePeriods.length > 0">
            <div
              v-for="(item, index) in activePeriods"
              :key="`ca-${index}`"
              class="caMonthlyRow"
            >
              <span>{{ item.plage }}</span>
              <b>{{ formatMoney(item.ca) }} TND</b>
            </div>
          </div>

          <div class="caEmptyState" v-else>
            Aucune facture sur la période choisie.
          </div>
        </div>

        <div class="managerPopup-card caStatCard">
          <div class="caStatEyebrow">Volume</div>
          <div class="caStatValue">{{ totalNbr }}</div>
          <div class="caStatUnit">facture(s)</div>

          <div class="caMonthlyList" v-if="activePeriods.length > 0">
            <div
              v-for="(item, index) in activePeriods"
              :key="`nbr-${index}`"
              class="caMonthlyRow"
            >
              <span>{{ item.plage }}</span>
              <b>{{ item.nbr }} facture(s)</b>
            </div>
          </div>

          <div class="caEmptyState" v-else>
            Aucune facture à comptabiliser sur cette plage.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from "vue";

export default {
  props: {
    __list: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },

  data() {
    return {
      result: [],
      dateIn: null,
      dateOut: null,
      months: [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Decembre",
      ],
    };
  },

  computed: {
    exerciceLabel() {
      return `Exercice ${Vue.$cookies.get("exercice")}`;
    },

    hasActiveFilter() {
      return Boolean(this.dateIn || this.dateOut);
    },

    activePeriods() {
      return this.result.filter((item) => item.valide);
    },

    totalCA() {
      const total = this.result.reduce((sum, item) => sum + Number(item.ca || 0), 0);
      return formatNumber(total);
    },

    totalNbr() {
      return this.result.reduce((sum, item) => sum + Number(item.nbr || 0), 0);
    },

    filterSummary() {
      if (this.dateIn && this.dateOut) {
        return `Période du ${this.dateIn} au ${this.dateOut}.`;
      }
      if (this.dateIn) {
        return `Période à partir du ${this.dateIn}.`;
      }
      if (this.dateOut) {
        return `Période jusqu'au ${this.dateOut}.`;
      }
      return "Aucun filtre appliqué, toutes les factures de l'exercice sont prises en compte.";
    },
  },

  methods: {
    formatMoney(value) {
      return formatNumber(value);
    },

    normalizeDate(value) {
      if (!value) {
        return null;
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      date.setHours(0, 0, 0, 0);
      return date;
    },

    resetFilters() {
      this.dateIn = null;
      this.dateOut = null;
      this.parseList();
    },

    parseList() {
      const exercice = Vue.$cookies.get("exercice");
      const rangeStart = this.normalizeDate(this.dateIn);
      const rangeEnd = this.normalizeDate(this.dateOut);

      this.result = this.months.map((month, monthIndex) => {
        let nbrFact = 0;
        let totalFact = 0;

        this.$props.__list.forEach((facture) => {
          const createdAt = this.normalizeDate(facture.createdAt);
          if (!createdAt) {
            return;
          }

          if (rangeStart && createdAt < rangeStart) {
            return;
          }
          if (rangeEnd && createdAt > rangeEnd) {
            return;
          }

          if (createdAt.getUTCMonth() === monthIndex) {
            nbrFact += 1;
            totalFact += Number(facture.totalTTC || 0);
          }
        });

        return {
          plage: `${month} ${exercice}`,
          valide: nbrFact > 0,
          ca: totalFact,
          nbr: nbrFact,
        };
      });
    },
  },

  mounted() {
    setTimeout(() => {
      this.parseList();
    }, 200);
  },
};

function formatNumber(value) {
  const amount = Number(value || 0);
  return amount.toFixed(3);
}
</script>
<style scoped>
.caPopup {
  width: 100%;
}

.caPopupHead {
  align-items: center;
}

.caPopupBadge {
  flex: 0 0 auto;
  padding: 8px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #1e2a44 0%, #31415f 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  box-shadow: 0 10px 24px rgba(30, 42, 68, 0.18);
}

.caFilterCard {
  padding: 18px;
}

.caFilterGrid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 16px;
  align-items: end;
}

.caFilterField label {
  display: block;
  margin-bottom: 8px;
  color: #475569;
  font-size: 14px;
  font-weight: 700;
}

.caFilterField :deep(.form-control) {
  min-height: 46px;
  border-radius: 14px;
  border-color: #dbe4f0;
  box-shadow: none;
}

.caFilterField.datePick :deep(.b-form-btn-label-control) {
  display: flex !important;
  align-items: stretch;
  flex-wrap: nowrap;
  position: relative;
  width: 100%;
  min-height: 46px;
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  background: #fff;
  box-shadow: none;
  overflow: visible;
}

.caFilterField.datePick :deep(.b-form-btn-label-control > .btn) {
  flex: 0 0 46px;
  width: 46px;
  min-width: 46px;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  border-right: 1px solid #dbe4f0 !important;
  border-radius: 0 !important;
  background: linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%) !important;
  color: #475569 !important;
  box-shadow: none !important;
}

.caFilterField.datePick :deep(.b-form-btn-label-control > label.form-control) {
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  width: 1%;
  min-height: 44px;
  margin: 0 !important;
  padding: 0 14px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: #fff !important;
  color: #1e2a44 !important;
  text-align: left !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px !important;
  font-weight: 600;
  box-shadow: none !important;
}

.caFilterField.datePick :deep(.b-form-btn-label-control > .btn),
.caFilterField.datePick :deep(.b-form-btn-label-control > label.form-control) {
  position: relative;
  z-index: 2;
}

.caFilterField.datePick :deep(.b-form-btn-label-control > label.form-control.text-muted) {
  color: #94a3b8 !important;
  font-weight: 500;
}

.caFilterField.datePick :deep([id$="__dialog_"]) {
  position: absolute !important;
  top: calc(100% + 8px) !important;
  left: 0 !important;
  margin: 0 !important;
  z-index: 30 !important;
}

.caFilterField.datePick :deep(.dropdown-menu) {
  border: 1px solid #dbe4f0;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 22px 44px rgba(15, 23, 42, 0.18);
  padding: 10px;
}

.caFilterField.datePick :deep(.b-calendar-nav) {
  gap: 6px;
  margin-bottom: 8px;
}

.caFilterField.datePick :deep(.b-calendar-nav .btn) {
  min-height: 32px;
  padding: 6px 8px !important;
  border: 1px solid #dbe4f0 !important;
  border-radius: 10px !important;
  background: #fff !important;
  color: #475569 !important;
  box-shadow: none !important;
}

.caFilterField.datePick :deep(.b-calendar-grid-caption) {
  color: #334155;
  font-size: 14px;
  font-weight: 700;
}

.caFilterField.datePick :deep(.b-calendar-grid-weekdays small) {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}

.caFilterField.datePick :deep(.b-calendar-grid-body .btn) {
  color: #475569 !important;
  background: #fff !important;
  border: 1px solid transparent !important;
  box-shadow: none !important;
}

.caFilterField.datePick :deep(.b-calendar-grid-body .btn.active),
.caFilterField.datePick :deep(.b-calendar-grid-body [aria-selected="true"] .btn) {
  color: #fff !important;
  background: #1d4ed8 !important;
  border-color: #1d4ed8 !important;
}

.caFilterField.datePick :deep(.b-calendar-grid-body .btn.btn-outline-primary) {
  color: #1d4ed8 !important;
  border-color: #bfdbfe !important;
  background: #eff6ff !important;
}

.caFilterField.datePick :deep(.b-calendar-grid-help) {
  color: #94a3b8;
}

.caFilterActions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.caFilterBtn {
  min-width: 94px;
  min-height: 42px;
  border-radius: 12px;
  font-weight: 700;
}

.caFilterMeta {
  margin-top: 14px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
}

.caStatsGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.caStatCard {
  padding: 18px;
}

.caStatEyebrow {
  margin-bottom: 8px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.caStatValue {
  color: #16a34a;
  font-size: 34px;
  line-height: 1;
  font-weight: 700;
}

.caStatUnit {
  margin-top: 6px;
  color: #64748b;
  font-size: 13px;
}

.caMonthlyList {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.caMonthlyRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, #fbfdff 0%, #f4f8fc 100%);
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 13px;
}

.caMonthlyRow b {
  color: #1e2a44;
  white-space: nowrap;
}

.caEmptyState {
  margin-top: 18px;
  padding: 14px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px dashed #dbe4f0;
  color: #64748b;
  font-size: 13px;
}

@media (max-width: 991px) {
  .caFilterGrid {
    grid-template-columns: 1fr;
  }

  .caFilterActions {
    justify-content: flex-start;
  }

  .caStatsGrid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .caPopupHead {
    align-items: flex-start;
  }

  .caFilterCard,
  .caStatCard {
    padding: 14px;
  }

  .caFilterActions {
    width: 100%;
    flex-wrap: wrap;
  }

  .caFilterBtn {
    flex: 1 1 calc(50% - 10px);
  }

  .caStatValue {
    font-size: 28px;
  }

  .caMonthlyRow {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
